import React from "react";
import Button from "../Button";
import ActionDropdown, { ActionMenuItem } from "./ActionDropdown";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (_item: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  emptyMessage?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  onRowClick?: (_item: T) => void;
  actions?: (_item: T) => ActionMenuItem[];
  className?: string;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  pageSize?: number;
  onPageChange?: (_page: number) => void;
  showPagination?: boolean;
}

function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  loading = false,
  emptyMessage = "No data available",
  emptyActionLabel,
  onEmptyAction,
  onRowClick,
  actions,
  className = "",
  currentPage = 0,
  totalPages = 1,
  totalItems = 0,
  pageSize = 10,
  onPageChange,
  showPagination = true,
}: DataTableProps<T>) {
  const getCellValue = (item: T, column: Column<T>) => {
    if (column.render) {
      return column.render(item);
    }

    const keys = column.key.toString().split(".");
    let value: unknown = item;

    for (const key of keys) {
      value = (value as Record<string, unknown>)?.[key];
    }

    return (value as React.ReactNode) ?? "-";
  };

  const getRowActions = (item: T): ActionMenuItem[] => {
    return actions ? actions(item) : [];
  };

  const startItem = Math.min(currentPage * pageSize + 1, totalItems);
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  if (loading) {
    return (
      <div className={`bg-zinc-900 border border-zinc-800 ${className}`}>
        <div className="p-8 text-center">
          <div className="inline-block animate-spin h-8 w-8 border-b-2 border-amber-400 mb-4"></div>
          <p className="text-zinc-300">Loading...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={`bg-zinc-900 border border-zinc-800 ${className}`}>
        <div className="p-8 text-center">
          <p className="text-lg text-zinc-300 mb-4">{emptyMessage}</p>
          {onEmptyAction && emptyActionLabel && (
            <Button onClick={onEmptyAction} variant="primary" size="md">
              {emptyActionLabel}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-zinc-900 border border-zinc-800 ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-800/60 border-b border-zinc-700">
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider ${column.className || ""}`}
                  style={column.width ? { width: column.width } : undefined}
                >
                  <div className="flex items-center gap-2">
                    {column.header}
                    {column.sortable && (
                      <span className="text-zinc-500 cursor-pointer hover:text-amber-400">↕️</span>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {data.map((item, rowIndex) => {
              const rowActions = getRowActions(item);

              return (
                <tr
                  key={rowIndex}
                  className={`hover:bg-zinc-800/40 transition-colors ${onRowClick ? "cursor-pointer" : ""}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={`px-4 py-3 text-zinc-300 ${column.className || ""}`}
                    >
                      {getCellValue(item, column)}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-4 py-3 text-right">
                      {rowActions.length > 0 && (
                        <ActionDropdown items={rowActions} className="flex justify-end" />
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPagination && onPageChange && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
          <p className="text-sm text-zinc-500">
            Showing {startItem}–{endItem} of {totalItems} items
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage === 0}
              onClick={() => onPageChange(currentPage - 1)}
            >
              Previous
            </Button>
            <span className="text-xs text-zinc-400 px-2">
              Page {currentPage + 1} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages - 1}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
